import * as React from 'react';
import { Theme, createStyles, makeStyles } from '@material-ui/core';

// Shared "label : value" table treatment — a bordered box with one field per row (label cell
// on the left, value cell filling the rest), a divider line between rows. Introduced for
// MatterGeneralInfo.tsx and reused everywhere else Matter Details showed the same kind of
// label:value list (PartyCommon.tsx, DataTopicInfoCard.tsx) so every tab gets the same look.
const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        table: {
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 8,
            overflow: 'hidden',
            backgroundColor: theme.palette.background.paper,
        },
        row: {
            display: 'flex',
            '&:not(:last-child)': {
                borderBottom: `1px solid ${theme.palette.divider}`,
            },
        },
        labelCell: {
            width: 220,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            padding: theme.spacing(1.25, 2),
            backgroundColor: theme.palette.grey[50],
            borderRight: `1px solid ${theme.palette.divider}`,
            fontSize: 12.5,
            fontWeight: 600,
            color: theme.palette.text.secondary,
        },
        valueCell: {
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            padding: theme.spacing(1.25, 2),
            fontSize: 13.5,
            color: theme.palette.text.primary,
            whiteSpace: 'pre-line',
            wordBreak: 'break-word',
        },
    })
);

export interface BorderedInfoTableField {
    label: React.ReactNode;
    value: React.ReactNode;
}

export interface BorderedInfoTableProps {
    fields: BorderedInfoTableField[];
    className?: string;
}

export const BorderedInfoTable: React.FC<BorderedInfoTableProps> = ({ fields, className }) => {
    const classes = useStyles();

    if (fields.length === 0) {
        return null;
    }

    return (
        <div className={className ? `${classes.table} ${className}` : classes.table}>
            {fields.map((field, index) => (
                <div key={index} className={classes.row}>
                    <div className={classes.labelCell}>{field.label}</div>
                    <div className={classes.valueCell}>{field.value}</div>
                </div>
            ))}
        </div>
    );
};
